import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";
import { AccessTime, ErrorOutline, TrendingUp } from "@mui/icons-material";
import styled from "@emotion/styled";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import AddTimeRecordDialog from "../components/AddTimeRecordDialog";
import { buildDayColumns, backfillDayKeys, overTargetDayKeys, underTargetDayKeys, hasDailyTarget } from "../components/RunnitDailyHours";
import { getMyDailyHours } from "../redux/ducks/reports";
import { postTime } from "../redux/ducks/time";
import { showAlert } from "../redux/slices/alerts";
import { toLocalDay } from "../utils/dateUtils";

const ContainerStyled = styled(Container)(({ theme }) => ({
    padding: theme.spacing(3, 2),
    width: "100%",
    marginRight: "auto",
    marginLeft: "auto",
}));

const StatStrip = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
}));

const MonthSwitch = styled(ToggleButtonGroup)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const StateBox = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
}));

// Thirty-one near-identical rows give the eye nothing to land on, so today is marked — a tinted
// row with a coloured edge, which survives being read in either theme.
const DayRow = styled(TableRow, { shouldForwardProp: (prop) => !["$muted", "$today"].includes(prop) })(({ theme, $muted, $today }) => ({
    ...($muted && { backgroundColor: theme.palette.action.hover }),
    ...($today && {
        backgroundColor: theme.palette.action.selected,
        "& td:first-of-type": {
            boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
        },
    }),
}));

const TodayLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: theme.typography.fontWeightMedium,
}));

const DayCell = styled(TableCell)(({ theme }) => ({
    whiteSpace: "nowrap",
}));

const WeekdayLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

// The dark shade of a palette colour is tuned for light backgrounds; on the dark theme it drops
// under the contrast floor for body text, so the lighter shade carries the tone there instead.
const NumberCell = styled(TableCell, { shouldForwardProp: (prop) => prop !== "$tone" })(({ theme, $tone }) => ({
    textAlign: "right",
    whiteSpace: "nowrap",
    ...($tone && {
        color: theme.palette.mode === "dark" ? theme.palette[$tone].light : theme.palette[$tone].dark,
        fontWeight: theme.typography.fontWeightMedium,
    }),
}));

const ActionCell = styled(TableCell)({
    textAlign: "right",
    width: 140,
});

const Placeholder = styled("span")(({ theme }) => ({
    color: theme.palette.text.disabled,
}));

const EmptyMonthNote = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

// The loudest signal on the page, so it stays a solid red fill. MUI would pair error.main with
// white, which only reaches 3.7:1 against the dark palette's brighter red — under AA. Each mode
// gets the text colour that carries the fill it actually has.
const MissingChip = styled(Chip)(({ theme }) => ({
    backgroundColor: theme.palette.error.main,
    color: theme.palette.mode === "dark" ? "rgba(0, 0, 0, 0.87)" : theme.palette.common.white,
    fontWeight: theme.typography.fontWeightMedium,
}));

const monthRange = (offset) => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const end = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
    return {
        start: toLocalDay(start),
        end: toLocalDay(end),
        label: start.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
    };
};

const formatHours = (value) => (value ? value.toFixed(2) : null);

const todayKey = () => toLocalDay();

const MyHours = () => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [logDate, setLogDate] = useState(null);
    const loadRef = useRef(0);

    const isLastMonth = searchParams.get("month") === "last";
    const period = useMemo(() => monthRange(isLastMonth ? -1 : 0), [isLastMonth]);

    const load = useCallback(() => {
        const requestId = ++loadRef.current;
        setLoading(true);
        dispatch(
            getMyDailyHours(period.start, period.end, (data) => {
                if (loadRef.current !== requestId) return;
                setRow(data || null);
                setLoading(false);
            })
        );
    }, [dispatch, period.start, period.end]);

    useEffect(() => {
        load();
    }, [load]);

    const days = useMemo(() => buildDayColumns(period.start, period.end), [period.start, period.end]);
    const missingKeys = useMemo(() => new Set(row ? backfillDayKeys(row, days) : []), [row, days]);
    const underKeys = useMemo(() => new Set(row ? underTargetDayKeys(row, days) : []), [row, days]);
    const overKeys = useMemo(() => new Set(row ? overTargetDayKeys(row, days) : []), [row, days]);
    const holidayKeys = useMemo(() => new Set(row?.holidays || []), [row]);

    const handleMonthChange = (event, value) => {
        if (!value) return;
        setSearchParams(value === "last" ? { month: "last" } : {});
    };

    const handleAddTime = (timeData, target) => {
        if (!target?.project?._id) return;
        dispatch(
            postTime("project", target.project._id, timeData, (response) => {
                if (response && response.timeRecord) {
                    dispatch(showAlert({ severity: "success", message: "Time record added" }));
                    load();
                } else {
                    dispatch(showAlert({ severity: "error", message: response?.message || "Error adding time record" }));
                }
            })
        );
    };

    const target = row?.expectedDailyHours || 0;
    const today = todayKey();

    return (
        <ContainerStyled maxWidth="lg">
            <PageHeader
                title="My Hours"
                subtitle="Your own hours day by day, so a heavy week cannot hide a day you never logged."
            />

            <MonthSwitch value={isLastMonth ? "last" : "this"} exclusive onChange={handleMonthChange} size="small">
                <ToggleButton value="this">This month</ToggleButton>
                <ToggleButton value="last">Last month</ToggleButton>
            </MonthSwitch>

            {loading ? (
                <Paper>
                    <StateBox>
                        <CircularProgress size={28} />
                        <Typography variant="body2">Loading your hours…</Typography>
                    </StateBox>
                </Paper>
            ) : !row ? (
                <Paper>
                    <StateBox>
                        <Typography variant="body2">We could not load your hours. Refresh the page or contact your manager.</Typography>
                    </StateBox>
                </Paper>
            ) : (
                <>
                    <StatStrip>
                        <StatCard
                            label={`Total logged in ${period.label}`}
                            value={`${(row.total || 0).toFixed(2)}h`}
                            tone="neutral"
                            icon={<AccessTime fontSize="small" />}
                        />
                        <StatCard
                            label="Days with no time logged"
                            value={missingKeys.size}
                            sub="Past weekdays, excluding time off and holidays"
                            tone={missingKeys.size ? "error" : "neutral"}
                            icon={<ErrorOutline fontSize="small" />}
                        />
                        <StatCard
                            label="Days over your target"
                            value={overKeys.size}
                            sub={`More than ${target}h logged`}
                            tone={overKeys.size ? "warn" : "neutral"}
                            icon={<TrendingUp fontSize="small" />}
                        />
                    </StatStrip>

                    {!row.total && missingKeys.size > 0 && (
                        <EmptyMonthNote severity="info">
                            {`Nothing at all is logged for ${period.label}, so every weekday that has passed shows as a gap. If you had not started work yet in this period, there is nothing to backfill.`}
                        </EmptyMonthNote>
                    )}

                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Day</TableCell>
                                    <TableCell align="right">Hours</TableCell>
                                    <TableCell align="right">Target</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Backfill</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {days.map((day) => {
                                    const hours = row.daily?.[day.key] || 0;
                                    const leave = row.off?.[day.key];
                                    const isHoliday = holidayKeys.has(day.key);
                                    const isMissing = missingKeys.has(day.key);
                                    const isUnder = underKeys.has(day.key);
                                    const isFuture = day.key > today;
                                    const isToday = day.key === today;
                                    const noTargetDay = !hasDailyTarget(row, day);

                                    let status = null;
                                    if (leave) status = <Chip size="small" variant={hours ? "outlined" : "filled"} label={`Off — ${leave}`} />;
                                    else if (isHoliday) status = <Chip size="small" variant={hours ? "outlined" : "filled"} label="Company holiday" />;
                                    else if (isFuture) status = null;
                                    else if (isMissing) status = <MissingChip size="small" label="No time logged" />;
                                    else if (!hours && day.isWeekend) status = null;
                                    else if (isToday && !hours) status = <Chip size="small" variant="outlined" label="In progress" />;
                                    else if (!hours) status = null;
                                    else if (overKeys.has(day.key)) status = <Chip size="small" color="warning" label="Over target" />;
                                    else if (hours >= target) status = <Chip size="small" color="success" label="Target met" />;
                                    else status = <Chip size="small" color="warning" variant="outlined" label="Under target" />;

                                    const hoursTone = !hours || noTargetDay ? undefined : overKeys.has(day.key) ? "warning" : hours >= target ? "success" : undefined;

                                    return (
                                        <DayRow key={day.key} $muted={noTargetDay && !isToday} $today={isToday} hover>
                                            <DayCell>
                                                {day.label} <WeekdayLabel variant="caption" component="span">{day.weekdayLabel}</WeekdayLabel>
                                                {isToday && <TodayLabel variant="caption" component="span"> · Today</TodayLabel>}
                                                {isHoliday && <WeekdayLabel variant="caption" component="span"> · Holiday</WeekdayLabel>}
                                                {leave && <WeekdayLabel variant="caption" component="span"> · {leave}</WeekdayLabel>}
                                            </DayCell>
                                            <NumberCell $tone={hoursTone}>
                                                {formatHours(hours) || <Placeholder>—</Placeholder>}
                                            </NumberCell>
                                            <NumberCell>{noTargetDay ? <Placeholder>—</Placeholder> : `${target}h`}</NumberCell>
                                            <TableCell>{status}</TableCell>
                                            <ActionCell>
                                                {(isMissing || isUnder) && (
                                                    <Tooltip title={isMissing ? `Log time against ${day.label}` : `Add more time to ${day.label}, which is under your ${target}h target`}>
                                                        <Button size="small" variant="outlined" onClick={() => setLogDate(day.key)}>
                                                            Log time
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </ActionCell>
                                        </DayRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}

            <AddTimeRecordDialog
                open={!!logDate}
                onClose={() => setLogDate(null)}
                onAddTime={handleAddTime}
                defaultDate={logDate || undefined}
                pickTarget
            />
        </ContainerStyled>
    );
};

export default MyHours;
